import * as React from "react";
import { Accordion } from "baseui/accordion";
import { Block } from "baseui/block";
import { Button } from "kanopi-components/Button";
import { Checkbox } from "baseui/checkbox";
import { Exposure } from "./Exposure";
import { ExposureFlowModal } from "./ExposureFlowModal";
import { HeadingSmall, ParagraphSmall } from "kanopi-components/typography";
import { HorizontalSpacer } from "kanopi-components/SpaceWrapper/HorizontalSpacer";
import { Plus } from "baseui/icon";
import { QuoteDocumentPollList } from "kw/util/DocumentPollList/DocumentPollList";
import { QuoteState } from "kw/api/zeus";
import { Tab, Tabs } from "baseui/tabs-motion";
import { useStyletron } from "kanopi-components/theme/useStyletron";
import { useToggleEnabledProducts } from "../hooks/useToggleEnabledProducts";
import { VerticalSpacer } from "kanopi-components/SpaceWrapper/VerticalSpacer";

type ProductsProps = {
  editable?: boolean;
  riskProfile: TRiskProfile;
  quotes: TQuote[];
  priceLoading: boolean;
  priceRiskProfile: () => void;
};
export const Products: React.FC<ProductsProps> = ({
  editable = true,
  priceLoading,
  riskProfile,
  priceRiskProfile,
  quotes,
}) => {
  const [, theme] = useStyletron();
  const showProductToggle =
    quotes?.filter((quote) => quote?.state !== QuoteState.KNOCKOUT)?.length > 1;
  const { toggleEnabledProducts, loading: togglingEnabledProducts } =
    useToggleEnabledProducts(riskProfile?.id, priceRiskProfile);
  const enabledProductKeys = riskProfile?.enabledProducts?.map((p) => p.key) || [];
  const [activeKey, setActiveKey] = React.useState("0");
  return riskProfile?.availableProducts?.length > 1 ? (
    <Tabs
      activeKey={activeKey}
      onChange={({ activeKey }) => {
        setActiveKey(activeKey);
      }}
      overrides={{
        TabList: {
          style: {
            justifyContent: "center",
            gridColumnGap: theme.sizing.scale400,
          },
        },
        TabBorder: {
          style: {
            display: "none",
          },
        },
        TabHighlight: {
          style: {
            display: "none",
          },
        },
      }}
      activateOnFocus
    >
      {quotes?.map((quote, index) => {
        const isQuoteInKnockout = quote?.state === QuoteState.KNOCKOUT || false;
        return (
          <Tab
            key={index}
            title={
              showProductToggle && !isQuoteInKnockout ? (
                <HorizontalSpacer data-testid={quote?.product?.key + "-toggle"}>
                  <Checkbox
                    checked={enabledProductKeys.includes(quote?.product?.key)}
                    onChange={(e) => {
                      toggleEnabledProducts(quote?.product?.key, e.target.checked);
                    }}
                    disabled={
                      togglingEnabledProducts ||
                      priceLoading ||
                      (enabledProductKeys?.length <= 1 &&
                        enabledProductKeys.includes(quote?.product?.key))
                    }
                    checkmarkType="toggle_round"
                  />
                  {quote?.product?.name}
                </HorizontalSpacer>
              ) : (
                quote?.product?.name
              )
            }
            overrides={{
              Tab: {
                style: ({ $isActive }) => ({
                  backgroundColor: $isActive
                    ? theme.componentOverrides.QuoteTabs.activeBackgroundColor
                    : theme.componentOverrides.QuoteTabs.inactiveBackgroundColor,
                  ":hover": {
                    backgroundColor:
                      theme.componentOverrides.QuoteTabs.activeHoverBackgroundColor,
                    color: theme.componentOverrides.QuoteTabs.activeHoverTextColor,
                  },
                  color: $isActive
                    ? theme.componentOverrides.QuoteTabs.activeTextColor
                    : theme.componentOverrides.QuoteTabs.textColor,
                  borderTopRightRadius: theme.borders.radius300,
                  borderTopLeftRadius: theme.borders.radius300,
                  borderColor: $isActive
                    ? theme.componentOverrides.QuoteTabs.activeBackgroundColor
                    : theme.componentOverrides.QuoteTabs.inactiveBorderColor,
                  borderTopWidth: $isActive ? 0 : theme.borders.border600.borderWidth,
                  borderRightWidth: $isActive ? 0 : theme.borders.border600.borderWidth,
                  borderBottomWidth: 0,
                  borderLeftWidth: $isActive ? 0 : theme.borders.border600.borderWidth,
                  borderStyle: theme.borders.border600.borderStyle,
                }),
              },
              TabPanel: {
                style: {
                  ...theme.borders.border600,
                  borderRadius: theme.borders.radius300,
                },
              },
            }}
          >
            <Product
              editable={editable}
              isQuoteInKnockout={isQuoteInKnockout}
              priceLoading={priceLoading}
              quote={quote}
              riskProfile={riskProfile}
            />
          </Tab>
        );
      })}
    </Tabs>
  ) : (
    <Product
      editable={editable}
      isQuoteInKnockout={quotes?.[0]?.state === QuoteState.KNOCKOUT || false}
      priceLoading={priceLoading}
      quote={quotes?.[0]}
      riskProfile={riskProfile}
      priceRiskProfile={priceRiskProfile}
    />
  );
};

type ProductProps = {
  editable: boolean;
  isQuoteInKnockout: boolean;
  riskProfile: TRiskProfile;
  priceLoading: boolean;
  priceRiskProfile: () => void;
  quote: TQuote;
};
const Product: React.FC<ProductProps> = ({
  editable,
  isQuoteInKnockout,
  riskProfile,
  priceLoading,
  priceRiskProfile,
  quote,
}) => {
  const [, theme] = useStyletron();
  const [openExposureFlowModal, setOpenExposureFlowModal] = React.useState(false);
  const addExposureType = riskProfile?.enabledExposures?.find(
    (exp) => !exp?.is_policy_level
  );
  const canAddExposure =
    riskProfile?.enabledExposures?.filter((exp) => exp?.type === addExposureType?.type)
      ?.length < addExposureType?.limit;

  return (
    <>
      {isQuoteInKnockout ? (
        <ParagraphSmall>
          Sorry we are unable to provide a quote for this product
        </ParagraphSmall>
      ) : (
        <VerticalSpacer>
          <Accordion>
            {riskProfile?.enabledExposures?.map((enabledExposure, index) => {
              if (enabledExposure?.product_key === quote?.product?.key) {
                return (
                  <Block
                    key={index}
                    data-testid={enabledExposure?.key + "-accordion"}
                    paddingTop={theme.sizing.scale1000}
                  >
                    <Exposure
                      editable={editable}
                      quote={quote}
                      riskProfile={riskProfile}
                      exposureItem={enabledExposure}
                      priceLoading={priceLoading}
                    />
                  </Block>
                );
              }
            })}
          </Accordion>
          {canAddExposure && editable && (
            <Block
              display="flex"
              justifyContent="center"
              paddingTop={theme.sizing.scale600}
            >
              <Button
                onClick={() => {
                  setOpenExposureFlowModal(true);
                }}
                wide
                shape="pill"
                size="compact"
                startEnhancer={() => (
                  <Block
                    overrides={{
                      Block: {
                        style: {
                          ...theme.borders.border600,
                          borderColor: theme.colors.buttonPrimaryText,
                          borderWidth: "2px",
                          borderRadius: "50%",
                          width: theme.sizing.scale800,
                          height: theme.sizing.scale800,
                        },
                      },
                    }}
                  >
                    <Plus size={theme.sizing.scale800} />
                  </Block>
                )}
                overrides={{
                  BaseButton: {
                    style: {
                      justifyContent: "space-between",
                    },
                  },
                }}
              >
                {`Add another ${addExposureType?.type_name?.toLowerCase()}`}
                <Block width={theme.sizing.scale800}></Block>
              </Button>
            </Block>
          )}

          {openExposureFlowModal && (
            <ExposureFlowModal
              isOpen={openExposureFlowModal}
              riskProfileId={riskProfile?.id}
              openModal={setOpenExposureFlowModal}
              title={`Add a ${addExposureType?.type_name?.toLowerCase()}`}
              exposureItem={addExposureType}
              onComplete={priceRiskProfile}
              loading={priceLoading}
            />
          )}
        </VerticalSpacer>
      )}
    </>
  );
};
